import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { UserPlus, Mail } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import BASE_URL from '../../config';

function InviteUserModal({ open, onClose, organisationId, onSuccess }) {
  const [email, setEmail] = useState('');
  const [roleCode, setRoleCode] = useState('');
  const [roles, setRoles] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Fetch roles on mount
  useEffect(() => {
    if (open && organisationId) {
      fetchRoles();
    }
  }, [open, organisationId]);

  const fetchRoles = async () => {
    if (!organisationId) {
      toast.error('Organisation not found. Please refresh the page.');
      return;
    }
    setLoadingRoles(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await axios.get(`${BASE_URL}/api/roles-options`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { organisation_id: organisationId },
      });
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const validateEmail = (email) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
  };

  const handleInvite = async () => {
    if (!email || !roleCode) {
      toast.error('Please enter email and select a role');
      return;
    }

    if (!validateEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setInviting(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      await axios.post(
        `${BASE_URL}/api/organizations/${organisationId}/invite`,
        {
          email: email.trim().toLowerCase(),
          role_code: roleCode,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Invitation sent to ${email}!`);
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRoleCode('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
        <UserPlus size={22} />
        Invite User to Organization
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          Enter the email address of the user you want to invite. They must have an existing account to be added to your organization.
        </Alert>

        {/* Email Input */}
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter user's email address"
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Mail size={18} style={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Role Selection */}
        <FormControl fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
            label="Role"
            disabled={loadingRoles}
          >
            {roles.map((role) => (
              <MenuItem key={role.role_code} value={role.role_code}>
                {role.role_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleInvite}
          disabled={!email || !roleCode || inviting}
          startIcon={inviting ? <CircularProgress size={16} /> : <UserPlus size={16} />}
        >
          {inviting ? 'Inviting...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteUserModal;
